import LoadingButton from "@mui/lab/LoadingButton";
import { TableRow, TableCell, Box, Button } from "@mui/material";
import { SettingTextfield } from "./SettingTextField";
import { useTranslation } from "../../../../../hooks/useTranslation";
import { useFormContext } from "react-hook-form";
import React from "react";

type EditableSettingRowProps = {
  isLoading: boolean;
  onClose: () => void;
  onSave: () => void;
  customHeader?: React.ReactNode;
};

export const EditableSettingRow = (props: EditableSettingRowProps) => {
  const { customHeader, isLoading, onClose, onSave } = props;
  const {
    formState: { isDirty, isValid },
    setError,
    clearErrors,
    getValues,
  } = useFormContext();

  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.stores.pricingSettings",
  );

  const maxValidation = { message: translate("errors.max"), value: 200 };

  const validateBounds = () => {
    const [lowerBound, upperBound] = [
      getValues("lowerBound"),
      getValues("upperBound"),
    ];

    if (lowerBound > upperBound) {
      setError("upperBound", {
        type: "manual",
        message: translate("errors.upperBoundLessThanLowerBound"),
      });
      return false;
    }

    clearErrors("upperBound");
    return true;
  };

  const validateProfitPercentages = () => {
    const [minProfit, standardProfit, maxProfit] = [
      getValues("minProfitPercentage"),
      getValues("standardProfitPercentage"),
      getValues("maxProfitPercentage"),
    ];

    if (minProfit >= standardProfit) {
      setError("standardProfitPercentage", {
        type: "manual",
        message: translate("errors.standardShouldBeGreaterThanMin"),
      });
      return false;
    }

    if (standardProfit >= maxProfit) {
      setError("standardProfitPercentage", {
        type: "manual",
        message: translate("errors.standardShouldBeLessThanMax"),
      });
      return false;
    }

    if (minProfit >= maxProfit) {
      setError("maxProfitPercentage", {
        type: "manual",
        message: translate("errors.maxShouldBeGreatest"),
      });
      return false;
    }

    clearErrors("minProfitPercentage");
    clearErrors("standardProfitPercentage");
    clearErrors("maxProfitPercentage");

    return true;
  };

  return (
    <TableRow sx={{ height: "75px" }}>
      <TableCell scope="row" align="center" width="250px">
        {customHeader ?? (
          <Box display="flex" alignItems="center" gap="4px">
            <SettingTextfield name="lowerBound" startAdornment="$" />
            -
            <SettingTextfield
              name="upperBound"
              startAdornment="$"
              required={false}
            />
          </Box>
        )}
      </TableCell>
      <TableCell align="center">
        <SettingTextfield
          name="minProfitPercentage"
          startAdornment="%"
          max={maxValidation}
        />
      </TableCell>
      <TableCell align="center">
        <SettingTextfield
          name="standardProfitPercentage"
          startAdornment="%"
          max={maxValidation}
        />
      </TableCell>
      <TableCell align="center">
        <SettingTextfield
          name="maxProfitPercentage"
          startAdornment="%"
          max={maxValidation}
        />
      </TableCell>
      <TableCell align="center">
        <Box display="flex" justifyContent="center">
          <LoadingButton
            loading={isLoading}
            size="small"
            color="primary"
            variant="contained"
            sx={{ marginRight: "8px" }}
            onClick={() => {
              if (validateBounds() && validateProfitPercentages()) {
                onSave();
              }
            }}
            disabled={!isDirty || !isValid}
          >
            {translateWithoutPrefix("buttons.save")}
          </LoadingButton>
          <Button
            disabled={isLoading}
            size="small"
            color="error"
            variant="contained"
            onClick={onClose}
          >
            {translateWithoutPrefix("buttons.cancel")}
          </Button>
        </Box>
      </TableCell>
    </TableRow>
  );
};
